import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { fetchNewsBySlug, stripHtml, formatDate } from '../../lib/wordpress';
import type { WPPost } from '../../lib/wordpress';
import CommonHeader from '../../components/feature/CommonHeader';
import CommonFooter from '../../components/feature/CommonFooter';

const BRAND = '#3E5F8F';

/* 記事内の「この記事を書いた人」付近の画像を小さく丸くする */
function styleAuthorImages() {
  const content = document.querySelector('.column-content');
  if (!content) return;

  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const targets: HTMLElement[] = [];
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    if (node.textContent?.includes('この記事を書いた人')) {
      const parent = node.parentElement;
      if (parent) targets.push(parent);
    }
  }

  targets.forEach((el) => {
    let sibling: Element | null = el.previousElementSibling;
    while (sibling) {
      const img =
        sibling.tagName === 'IMG'
          ? (sibling as HTMLImageElement)
          : sibling.querySelector('img');
      if (img) {
        img.style.width = '80px';
        img.style.height = '80px';
        img.style.borderRadius = '50%';
        img.style.objectFit = 'cover';
        img.style.margin = '0 auto';
        img.style.display = 'block';
        return;
      }
      sibling = sibling.previousElementSibling;
    }

    let parent: Element | null = el.parentElement;
    while (parent && parent !== content) {
      let pSibling: Element | null = parent.previousElementSibling;
      while (pSibling) {
        const img =
          pSibling.tagName === 'IMG'
            ? (pSibling as HTMLImageElement)
            : pSibling.querySelector('img');
        if (img) {
          img.style.width = '80px';
          img.style.height = '80px';
          img.style.borderRadius = '50%';
          img.style.objectFit = 'cover';
          img.style.margin = '0 auto';
          img.style.display = 'block';
          return;
        }
        pSibling = pSibling.previousElementSibling;
      }
      parent = parent.parentElement;
    }
  });
}

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<WPPost | null | undefined>(undefined);

  useEffect(() => {
    if (!slug) return;
    setPost(undefined);
    fetchNewsBySlug(slug).then(setPost);
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const title = `${stripHtml(post.title.rendered)} | さいわいデンタルクリニック moyuk SAPPORO`;
    const canonical = `https://saiwaidental.jp/moyuk/news/${post.slug}/`;
    document.title = title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', stripHtml(post.excerpt.rendered).slice(0, 120));
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
    el.setAttribute('href', canonical);

    requestAnimationFrame(() => styleAuthorImages());
    setTimeout(() => styleAuthorImages(), 300);
  }, [post]);

  if (post === undefined) {
    return (
      <div className="min-h-screen bg-white">
        <CommonHeader />
        <div className="pt-40 flex justify-center">
          <div className="space-y-4 w-full max-w-3xl px-6">
            <div className="bg-gray-100 h-8 w-2/3 rounded animate-pulse" />
            <div className="bg-gray-100 h-4 w-full rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (post === null) return <Navigate to="/news/" replace />;

  return (
    <div className="min-h-screen bg-white">
      <CommonHeader />

      <main className="pt-28 pb-24 px-6 max-w-3xl mx-auto">
        <nav className="text-xs text-gray-400 mb-6 flex flex-wrap items-center gap-1">
          <Link to="/" className="hover:text-gray-600 transition-colors">TOP</Link>
          <span>/</span>
          <Link to="/news/" className="hover:text-gray-600 transition-colors">お知らせ</Link>
          <span>/</span>
          <span className="text-gray-500 line-clamp-1" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
        </nav>

        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs px-3 py-1 rounded-full text-white" style={{ backgroundColor: BRAND }}>
            お知らせ
          </span>
          <time className="text-xs text-gray-400">{formatDate(post.date)}</time>
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-snug mb-8" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />

        <div className="column-content" dangerouslySetInnerHTML={{ __html: post.content.rendered }} />

        <div className="mt-10 pt-8 border-t border-gray-100">
          <Link to="/news/" className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: BRAND }}>
            ← お知らせ一覧に戻る
          </Link>
        </div>
      </main>

      <CommonFooter />
    </div>
  );
}
